import React, { useEffect, useState } from 'react';
import axios from 'axios';

const BASE_URL = process.env.REACT_APP_API_URL || 'https://education-crm-2b77a26760ff.herokuapp.com/api';
const NAVY = '#1B2A6B';
const GOLD = '#C9A84C';

const HIGHLIGHTS = [
  { icon: '🖥️', value: '450+', label: 'Hands-On Labs' },
  { icon: '⚡', value: '40 hrs', label: 'Personal GPU Time' },
  { icon: '💼', value: '100%', label: 'Internship Guaranteed' },
  { icon: '🏅', value: 'AIAAI', label: 'Certified' },
];

const COURSES = [
  'AI & Machine Learning',
  'Data Science & Analytics',
  'Full Stack Web Development',
  'Cloud Computing & DevOps',
  'Cybersecurity',
  'Robotics & IoT',
  'Computer Vision',
  'Natural Language Processing',
  'Not sure — help me decide',
];

const YEARS = ['1st Year', '2nd Year', '3rd Year', '4th Year', 'Final Year', 'Post Graduate', 'Working Professional'];

const inputStyle = {
  width: '100%', padding: '12px 14px', fontSize: 15, border: '1.5px solid #e2e8f0',
  borderRadius: 10, fontFamily: 'inherit', outline: 'none', boxSizing: 'border-box',
  background: '#fff', color: '#1e293b',
};
const labelStyle = { fontSize: 13, fontWeight: 600, color: '#475569', marginBottom: 4, display: 'block' };

export default function ApplyPage() {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState({
    full_name: '', phone: '', email: '', college_name: '', year_of_study: '', course_interest: '', city: '',
  });

  const urlParams = new URLSearchParams(window.location.search);
  const utmData = {
    utm_source: urlParams.get('utm_source'), utm_medium: urlParams.get('utm_medium'),
    utm_campaign: urlParams.get('utm_campaign'), utm_term: urlParams.get('utm_term'),
    utm_content: urlParams.get('utm_content'),
  };

  useEffect(() => {
    axios.get(`${BASE_URL}/funnel/apply/track-view`, { params: utmData }).catch(() => {});
  }, []);

  const set = (f, v) => setForm(p => ({ ...p, [f]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.full_name || !form.phone || !form.email) {
      setError('Please fill in your name, phone, and email.');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${BASE_URL}/funnel/student-interest`, { ...form, ...utmData });
      setSubmitted(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      setError(err.response?.data?.message || 'Submission failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) return (
    <div style={{ minHeight: '100vh', background: `linear-gradient(135deg, ${NAVY}, #2d3f8a)`, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
      <div style={{ background: '#fff', borderRadius: 20, padding: '48px 36px', textAlign: 'center', maxWidth: 480, width: '100%' }}>
        <div style={{ fontSize: 64, marginBottom: 16 }}>🚀</div>
        <h2 style={{ fontSize: 22, fontWeight: 800, color: NAVY, marginBottom: 10 }}>You're One Step Closer!</h2>
        <p style={{ color: '#64748b', fontSize: 15, lineHeight: 1.7 }}>
          A counsellor will contact you within <strong>24 hours</strong>. You may also qualify for the{' '}
          <strong style={{ color: GOLD }}>AI Pathfinder Scholarship</strong> worth up to ₹26,000!
        </p>
        <div style={{ marginTop: 24, padding: '16px', background: '#f8fafc', borderRadius: 12 }}>
          <p style={{ fontSize: 13, color: '#64748b', margin: 0 }}>📲 Keep your phone ready — we'll call you shortly!</p>
        </div>
        <div style={{ marginTop: 20, display: 'flex', gap: 8, justifyContent: 'center', flexWrap: 'wrap' }}>
          {['✅ GPU Lab Access', '🎓 Scholarship Available', '💼 Internship Guaranteed'].map(t => (
            <span key={t} style={{ fontSize: 13, background: '#f0fdf4', color: '#15803d', padding: '5px 12px', borderRadius: 20, fontWeight: 600 }}>{t}</span>
          ))}
        </div>
      </div>
    </div>
  );

  return (
    <div style={{ minHeight: '100vh', fontFamily: 'Inter, system-ui, sans-serif', background: '#f8fafc' }}>
      <style>{`
        input:focus, select:focus { border-color: ${GOLD} !important; box-shadow: 0 0 0 3px rgba(201,168,76,0.15); }
      `}</style>

      {/* Hero */}
      <div style={{ background: `linear-gradient(135deg, ${NAVY} 0%, #2d3f8a 100%)`, padding: '60px 24px 80px', textAlign: 'center' }}>
        <div style={{ maxWidth: 700, margin: '0 auto' }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'rgba(201,168,76,0.15)', border: '1px solid rgba(201,168,76,0.4)', borderRadius: 20, padding: '6px 16px', marginBottom: 20 }}>
            <span style={{ color: GOLD, fontSize: 13, fontWeight: 700 }}>🎓 AI ENGINEERING PROGRAM</span>
          </div>
          <h1 style={{ fontSize: 'clamp(26px, 5vw, 44px)', fontWeight: 900, color: '#fff', lineHeight: 1.2, marginBottom: 14 }}>
            Start Your <span style={{ color: GOLD }}>AI Engineering Career</span>
          </h1>
          <p style={{ fontSize: 16, color: 'rgba(255,255,255,0.8)', lineHeight: 1.7, maxWidth: 540, margin: '0 auto 36px' }}>
            Real GPU labs. Live projects. Industry mentors. Get certified and placed in 6 months.
          </p>

          {/* Highlight Cards */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 10, maxWidth: 640, margin: '0 auto' }}>
            {HIGHLIGHTS.map(h => (
              <div key={h.label} style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: 12, padding: '16px 12px', backdropFilter: 'blur(4px)' }}>
                <div style={{ fontSize: 24, marginBottom: 6 }}>{h.icon}</div>
                <div style={{ fontSize: 20, fontWeight: 800, color: GOLD }}>{h.value}</div>
                <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.7)', fontWeight: 500 }}>{h.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div style={{ maxWidth: 900, margin: '0 auto', padding: '48px 24px 60px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24, alignItems: 'start' }}>

          {/* ROI Card */}
          <div>
            <h2 style={{ fontSize: 20, fontWeight: 800, color: NAVY, marginBottom: 16 }}>Program ROI</h2>
            <div style={{ background: `linear-gradient(135deg, ${NAVY}, #2d3f8a)`, borderRadius: 16, padding: '24px 22px', color: '#fff', marginBottom: 16 }}>
              <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.6)', fontWeight: 700, marginBottom: 18 }}>NOVA ERA LABS · AI PROGRAM</div>
              <div style={{ display: 'grid', gap: 14 }}>
                {[
                  { label: 'Program Fee', value: '₹1,26,000 + GST', color: '#fff' },
                  { label: 'Max Scholarship', value: '₹26,000 off', color: GOLD },
                  { label: 'Salary Before Program', value: '₹3.2 LPA avg', color: '#fca5a5' },
                  { label: 'Salary After Program', value: '₹6.8 LPA avg', color: '#86efac' },
                  { label: 'ROI Payback Period', value: '< 3 months', color: GOLD },
                ].map(item => (
                  <div key={item.label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid rgba(255,255,255,0.08)', paddingBottom: 10 }}>
                    <span style={{ fontSize: 13, color: 'rgba(255,255,255,0.7)' }}>{item.label}</span>
                    <span style={{ fontSize: 14, fontWeight: 800, color: item.color }}>{item.value}</span>
                  </div>
                ))}
              </div>
            </div>

            <div style={{ background: '#f0fdf4', border: '1.5px solid #bbf7d0', borderRadius: 12, padding: '16px' }}>
              <p style={{ fontSize: 13, fontWeight: 700, color: '#15803d', margin: '0 0 8px' }}>🏆 What you get:</p>
              <ul style={{ margin: 0, padding: '0 0 0 16px', fontSize: 13, color: '#15803d', lineHeight: 2 }}>
                <li>450+ curated AI lab exercises</li>
                <li>40 hours personal GPU server time</li>
                <li>Live mentorship from AI professionals</li>
                <li>Portfolio projects for job applications</li>
                <li>Internship placement guaranteed</li>
                <li>AIAAI industry certification</li>
              </ul>
            </div>
          </div>

          {/* Form */}
          <div style={{ background: '#fff', border: '1.5px solid #e2e8f0', borderRadius: 20, padding: '28px 24px', boxShadow: '0 4px 20px rgba(0,0,0,0.06)' }}>
            <h2 style={{ fontSize: 18, fontWeight: 800, color: NAVY, margin: '0 0 20px' }}>Register Your Interest</h2>
            <form onSubmit={handleSubmit}>
              <div style={{ display: 'grid', gap: 14 }}>
                <div>
                  <label style={labelStyle}>Full Name *</label>
                  <input style={inputStyle} placeholder="Your full name" value={form.full_name}
                    onChange={e => set('full_name', e.target.value)} required />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                  <div>
                    <label style={labelStyle}>Phone *</label>
                    <input style={inputStyle} type="tel" placeholder="9876543210" value={form.phone}
                      onChange={e => set('phone', e.target.value)} required />
                  </div>
                  <div>
                    <label style={labelStyle}>City</label>
                    <input style={inputStyle} placeholder="Your city" value={form.city}
                      onChange={e => set('city', e.target.value)} />
                  </div>
                </div>
                <div>
                  <label style={labelStyle}>Email *</label>
                  <input style={inputStyle} type="email" placeholder="you@email.com" value={form.email}
                    onChange={e => set('email', e.target.value)} required />
                </div>
                <div>
                  <label style={labelStyle}>College / University</label>
                  <input style={inputStyle} placeholder="Your college name" value={form.college_name}
                    onChange={e => set('college_name', e.target.value)} />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                  <div>
                    <label style={labelStyle}>Year of Study</label>
                    <select style={inputStyle} value={form.year_of_study} onChange={e => set('year_of_study', e.target.value)}>
                      <option value="">Select</option>
                      {YEARS.map(y => <option key={y} value={y}>{y}</option>)}
                    </select>
                  </div>
                  <div>
                    <label style={labelStyle}>Course Interest</label>
                    <select style={inputStyle} value={form.course_interest} onChange={e => set('course_interest', e.target.value)}>
                      <option value="">Select</option>
                      {COURSES.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                </div>

                {error && (
                  <div style={{ background: '#fee2e2', color: '#dc2626', padding: '10px 14px', borderRadius: 10, fontSize: 13 }}>{error}</div>
                )}

                <button type="submit" disabled={submitting}
                  style={{
                    padding: '14px', background: submitting ? '#94a3b8' : `linear-gradient(135deg, ${NAVY}, #2d3f8a)`,
                    color: '#fff', border: 'none', borderRadius: 12, fontSize: 15, fontWeight: 700,
                    cursor: submitting ? 'not-allowed' : 'pointer', fontFamily: 'inherit',
                  }}>
                  {submitting ? 'Submitting…' : '🚀 Start My AI Journey'}
                </button>
                <p style={{ textAlign: 'center', fontSize: 11, color: '#94a3b8', margin: 0 }}>
                  Counsellor will call within 24 hours · Free scholarship assessment
                </p>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div style={{ background: NAVY, padding: '24px', textAlign: 'center' }}>
        <div style={{ color: GOLD, fontWeight: 800, fontSize: 16, marginBottom: 4 }}>⚡ Nova Era Labs</div>
        <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: 12 }}>India's AI Education Platform</div>
      </div>
    </div>
  );
}
